import { useState, useEffect, useMemo } from "react";
import { ChevronLeft, ChevronRight, CalendarX2 } from "lucide-react";
import { toast } from "sonner";
import { fetchAvailability, blockDay, unblockDay } from "@/lib/api";
import { pad2, toDateKey, MONTH_NAMES, DOW_LABELS } from "@/components/AvailabilityPicker";

const AdminDayBlocker = () => {
  const today = new Date();
  const [view, setView] = useState({ y: today.getFullYear(), m: today.getMonth() });
  const [blocked, setBlocked] = useState([]);

  const monthKey = `${view.y}-${pad2(view.m + 1)}`;

  useEffect(() => {
    fetchAvailability(monthKey).then((r) => setBlocked(r.blocked || [])).catch(() => {});
  }, [monthKey]);

  const cells = useMemo(() => {
    const first = new Date(view.y, view.m, 1);
    const lead = (first.getDay() + 6) % 7;
    const days = new Date(view.y, view.m + 1, 0).getDate();
    const out = [];
    for (let i = 0; i < lead; i++) out.push(null);
    for (let d = 1; d <= days; d++) out.push(d);
    return out;
  }, [view]);

  const canPrev = view.y > today.getFullYear() || view.m > today.getMonth();
  const move = (dir) =>
    setView((v) => {
      const d = new Date(v.y, v.m + dir, 1);
      return { y: d.getFullYear(), m: d.getMonth() };
    });

  const toggle = async (key) => {
    const isBlocked = blocked.includes(key);
    setBlocked((prev) => (isBlocked ? prev.filter((x) => x !== key) : [...prev, key]));
    try {
      if (isBlocked) {
        await unblockDay(key);
        toast.success(`${key.split("-").reverse().join("/")} reopened for bookings.`);
      } else {
        await blockDay(key);
        toast.success(`${key.split("-").reverse().join("/")} blocked off.`);
      }
    } catch {
      setBlocked((prev) => (isBlocked ? [...prev, key] : prev.filter((x) => x !== key)));
      toast.error("Could not update that day.");
    }
  };

  return (
    <div data-testid="admin-block-calendar" className="bg-panel border border-edge rounded-2xl p-5 sm:p-6 mt-8">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2.5">
          <CalendarX2 size={16} className="text-volt" />
          <h2 className="font-syne font-bold uppercase tracking-widest text-sm text-slate-100">Block Days Off</h2>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-mono2 text-[10px] uppercase tracking-widest text-slate-500">
            {MONTH_NAMES[view.m]} {view.y}
          </span>
          <button
            data-testid="admin-block-prev-month"
            onClick={() => move(-1)}
            disabled={!canPrev}
            aria-label="Previous month"
            className="w-7 h-7 rounded-full border border-edge flex items-center justify-center text-slate-300 hover:border-volt hover:text-volt disabled:opacity-30 transition-colors"
          >
            <ChevronLeft size={13} />
          </button>
          <button
            data-testid="admin-block-next-month"
            onClick={() => move(1)}
            aria-label="Next month"
            className="w-7 h-7 rounded-full border border-edge flex items-center justify-center text-slate-300 hover:border-volt hover:text-volt transition-colors"
          >
            <ChevronRight size={13} />
          </button>
        </div>
      </div>
      <p className="font-mono2 text-[10px] uppercase tracking-widest text-slate-600 mb-4">
        Tap a day to close it — customers won't be able to book it
      </p>

      <div className="grid grid-cols-7 gap-1 text-center mb-1.5 max-w-md">
        {DOW_LABELS.map((d) => (
          <div key={d} className="font-mono2 text-[9px] uppercase tracking-widest text-slate-600 py-1">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1 max-w-md">
        {cells.map((d, i) => {
          if (!d) return <div key={`b${i}`} />;
          const key = toDateKey(view.y, view.m, d);
          const cellDate = new Date(view.y, view.m, d);
          const isPast = cellDate < new Date(today.getFullYear(), today.getMonth(), today.getDate());
          const isSunday = cellDate.getDay() === 0;
          const isBlocked = blocked.includes(key);
          if (isPast || isSunday) {
            return (
              <div key={key} className="aspect-square rounded-lg font-mono2 text-xs flex items-center justify-center text-slate-700">
                {d}
              </div>
            );
          }
          return (
            <button
              key={key}
              data-testid={`admin-block-day-${key}`}
              onClick={() => toggle(key)}
              aria-label={`${isBlocked ? "Unblock" : "Block"} ${key}`}
              className={`aspect-square rounded-lg font-mono2 text-xs transition-colors duration-200 flex items-center justify-center ${
                isBlocked
                  ? "bg-red-400/15 border border-red-400/60 text-red-300 line-through"
                  : "text-slate-300 border border-edge/60 hover:border-red-400/60 hover:text-red-300"
              }`}
            >
              {d}
            </button>
          );
        })}
      </div>
      <div className="mt-4 flex items-center gap-4 font-mono2 text-[9px] uppercase tracking-widest text-slate-600">
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm border border-edge" /> Open</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-red-400/40 border border-red-400/60" /> Blocked</span>
      </div>
    </div>
  );
};

export default AdminDayBlocker;
